import { useEffect, useState } from "react";
import { api } from "../../api/client";
import { useNotify } from "../../components/notifications";
import { AppDialog } from "../../components/AppDialog";
import { AreaDialog, type AreaRow } from "./AreaDialog";
import { LinkPickerDialog, type LinkOption } from "./LinkPickerDialog";

/** Standalone-create wiring: when supplied, OK POSTs the city (and its buffered areas) to the System
 *  Management endpoints, re-reads `optionsUrl`, and hands back the fresh option list + the new id to
 *  select — like CountryDialog. Omitted ⇒ the buffered `onOk` mode the Country/State dialogs use. */
export interface CityStandalone {
  optionsUrl: string;
  onAdded: (options: { id: string; name: string }[], selectedId: string) => void;
}

/** A City row buffered under a State. `oid` set ⇒ an EXISTING city being linked (re-parented on
 *  save). `stateOid` re-assigns the city to a DIFFERENT existing state (empty ⇒ keep the parent
 *  state of the cascade). `areas` are the M:N Area rows buffered under it (dbo.City_Areas). */
export interface CityRow {
  oid?: string;
  cityName: string;
  stateOid?: string;
  areas: AreaRow[];
}

interface Props {
  /** The row being edited (Edit), or undefined for a new city (New). */
  initial?: CityRow;
  /** Name of the state this city is being added under — the default of the State dropdown. */
  parentStateLabel?: string;
  /** Buffered mode (Country/State dialog): hand the row back to the parent, which persists it. */
  onOk?: (row: CityRow) => void;
  /** Standalone mode (a "+" on another form, e.g. Contact ▸ City): OK persists directly + selects. */
  standalone?: CityStandalone;
  onClose: () => void;
}

/**
 * The legacy "City" popup — the third level of Country ▸ State ▸ City ▸ Area (owner request
 * 2026-10-23, image 846). City Name* + a State dropdown (default = the parent state, re-assignable
 * to another existing state) + an Area child grid (New · Link · Unlink · Edit · Export to) whose
 * New / Edit open the {@link AreaDialog} and whose Link opens the shared {@link LinkPickerDialog}.
 * A pure in-memory editor: persisted on the Country dialog's final OK, where each city's areas are
 * created and linked via dbo.City_Areas.
 */
export function CityDialog({ initial, parentStateLabel, onOk, standalone, onClose }: Props) {
  const notify = useNotify();
  const [cityName, setCityName] = useState(initial?.cityName ?? "");
  const [stateOid, setStateOid] = useState(initial?.stateOid ?? "");
  const [areas, setAreas] = useState<AreaRow[]>(initial?.areas ?? []);
  const [sel, setSel] = useState<number | null>(null);
  const [tried, setTried] = useState(false);
  const [saving, setSaving] = useState(false);

  // Existing saved states, so the City can be re-assigned to a different one (legacy State combo).
  const [stateOptions, setStateOptions] = useState<LinkOption[]>([]);
  useEffect(() => {
    let alive = true;
    void (async () => {
      try {
        const { data } = await api.get<{ data?: { id: string; longName?: string }[] }>("/api/v1/system/states", {
          silentLoading: true,
        } as never);
        if (alive) setStateOptions((data?.data ?? []).map((s) => ({ id: s.id, label: s.longName ?? "" })));
      } catch {
        /* axios surfaced it */
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  // Nested Area popup (New / Edit).
  const [areaOpen, setAreaOpen] = useState(false);
  const [areaEdit, setAreaEdit] = useState<number | null>(null);
  // Area Link picker.
  const [linkOpen, setLinkOpen] = useState(false);
  const [linkOptions, setLinkOptions] = useState<LinkOption[]>([]);

  const addArea = () => {
    setAreaEdit(null);
    setAreaOpen(true);
  };
  const editArea = () => {
    if (sel == null) return;
    setAreaEdit(sel);
    setAreaOpen(true);
  };
  const unlinkArea = () => {
    if (sel == null) return;
    setAreas((a) => a.filter((_, j) => j !== sel));
    setSel(null);
  };
  const onAreaOk = (row: AreaRow) => {
    setAreas((a) => (areaEdit == null ? [...a, row] : a.map((r, j) => (j === areaEdit ? row : r))));
    setAreaOpen(false);
  };

  const openLink = async () => {
    try {
      const { data } = await api.get<{ data?: { id: string; areaName?: string }[] }>("/api/v1/system/areas", {
        silentLoading: true,
      } as never);
      const have = new Set(areas.map((r) => r.oid).filter(Boolean));
      setLinkOptions((data?.data ?? []).filter((r) => !have.has(r.id)).map((r) => ({ id: r.id, label: r.areaName ?? "" })));
      setLinkOpen(true);
    } catch {
      /* axios surfaced it */
    }
  };
  const confirmLink = (picked: LinkOption[]) => {
    setAreas((a) => [...a, ...picked.map((p) => ({ oid: p.id, areaName: p.label }))]);
    setLinkOpen(false);
  };

  const exportAreas = () => {
    const body = areas.map((r) => `"${r.areaName}"`).join("\n");
    const blob = new Blob([`Area\n${body}`], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `${cityName.trim() || "city"}-areas.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const ok = async () => {
    setTried(true);
    if (!cityName.trim()) return;
    if (!standalone) {
      onOk?.({ oid: initial?.oid, cityName: cityName.trim(), stateOid: stateOid || undefined, areas });
      return;
    }
    // Standalone create (Contact ▸ City "+"): persist the city (and any buffered areas) directly, then
    // re-read the host field's options + select the new city — the City-level slice of CountryDialog.
    setSaving(true);
    try {
      const cRes = await api.post<{ data?: { id?: string } }>("/api/v1/system/cities", {
        cityName: cityName.trim(), stateId: stateOid || undefined,
      });
      const cityId = cRes.data?.data?.id ?? "";
      for (const ar of areas.filter((x) => x.areaName.trim())) {
        const areaId = ar.oid ?? (await api.post<{ data?: { id?: string } }>("/api/v1/system/areas", { areaName: ar.areaName.trim() })).data?.data?.id ?? "";
        if (cityId && areaId) await api.post(`/api/v1/system/cities/${cityId}/areas/${areaId}/link`);
      }
      const { data } = await api.get<{ data?: { id: string; name: string }[] }>(standalone.optionsUrl, { silentLoading: true } as never);
      const fresh = data?.data ?? [];
      const selected =
        (cityId && fresh.find((o) => o.id.toLowerCase() === cityId.toLowerCase())?.id) ||
        fresh.find((o) => o.name === cityName.trim())?.id ||
        cityId;
      notify.success("Saved successfully.");
      standalone.onAdded(fresh, selected);
      onClose();
    } catch {
      /* axios error dialog already surfaced it */
    } finally {
      setSaving(false);
    }
  };

  return (
    <AppDialog
      open
      title="City"
      icon="ti-building-community"
      width={480}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="ca-btn" onClick={onClose}>
            Cancel
          </button>
          <button type="button" className="ca-btn ca-btn-solid" onClick={() => void ok()} disabled={saving}>
            OK
          </button>
        </>
      }
    >
      <div className="cd-form">
        <div className="cd-field">
          <label className="cd-label" htmlFor="city-name">
            City:<span className="req">*</span>
          </label>
          <input
            id="city-name"
            className={`op-input${tried && !cityName.trim() ? " required-empty" : ""}`}
            value={cityName}
            onChange={(e) => setCityName(e.target.value)}
          />
        </div>
        <div className="cd-field">
          <label className="cd-label" htmlFor="city-state">
            State:
          </label>
          <select id="city-state" className="op-input" value={stateOid} onChange={(e) => setStateOid(e.target.value)}>
            <option value="">{parentStateLabel || (standalone ? "— None —" : "— this state —")}</option>
            {stateOptions.map((s) => (
              <option key={s.id} value={s.id}>
                {s.label}
              </option>
            ))}
          </select>
        </div>

        <div className="cd-state">
          <div className="cd-state-h">Area</div>
          <div className="op-childbar">
            <button type="button" className="op-cb" onClick={addArea}>
              <i className="ti ti-plus" aria-hidden="true" /> New
            </button>
            <button type="button" className="op-cb" onClick={openLink}>
              <i className="ti ti-link" aria-hidden="true" /> Link
            </button>
            <button type="button" className={`op-cb${sel == null ? " muted" : ""}`} onClick={unlinkArea} disabled={sel == null}>
              <i className="ti ti-unlink" aria-hidden="true" /> Unlink
            </button>
            <button type="button" className={`op-cb${sel == null ? " muted" : ""}`} onClick={editArea} disabled={sel == null}>
              <i className="ti ti-pencil" aria-hidden="true" /> Edit
            </button>
            <button type="button" className={`op-cb${areas.length === 0 ? " muted" : ""}`} onClick={exportAreas} disabled={areas.length === 0}>
              <i className="ti ti-file-export" aria-hidden="true" /> Export to
            </button>
          </div>
          <table className="cd-state-tbl">
            <thead>
              <tr>
                <th className="cd-sel" aria-label="select" />
                <th className="cd-edit" aria-label="edit" />
                <th>Area</th>
              </tr>
            </thead>
            <tbody>
              {areas.length === 0 ? (
                <tr>
                  <td colSpan={3} className="cd-empty">
                    No data to display
                  </td>
                </tr>
              ) : (
                areas.map((r, i) => (
                  <tr key={i} className={sel === i ? "cd-rowsel" : undefined} onClick={() => setSel(i)}>
                    <td className="cd-sel">
                      <input type="checkbox" checked={sel === i} onChange={() => setSel(sel === i ? null : i)} />
                    </td>
                    <td className="cd-edit">
                      <button
                        type="button"
                        className="sd-link-edit"
                        title="Edit"
                        aria-label="Edit"
                        onClick={(e) => {
                          e.stopPropagation();
                          setAreaEdit(i);
                          setAreaOpen(true);
                        }}
                      >
                        <i className="ti ti-edit" />
                      </button>
                    </td>
                    <td>{r.areaName}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {areaOpen && (
        <AreaDialog
          initial={areaEdit != null ? areas[areaEdit] : undefined}
          onOk={onAreaOk}
          onClose={() => setAreaOpen(false)}
        />
      )}

      {linkOpen && (
        <LinkPickerDialog
          title="Link Area"
          options={linkOptions}
          columnLabel="Area"
          emptyText="No areas available to link"
          onOk={confirmLink}
          onClose={() => setLinkOpen(false)}
        />
      )}
    </AppDialog>
  );
}
